/**
 * TownScene -- Town hub with vendor interactions.
 *
 * PixiJS canvas: static town backdrop (simple colored background).
 * HTML/CSS DOM: town name, description, vendor buttons, vendor panel overlays.
 *
 * Enters from WorldTravel when arriving at a town node.
 * Each vendor button opens a panel overlay.
 *
 * @module TownScene
 */

import { Container, Graphics, Text } from 'pixi.js';
import debugLog from '../debug/DebugLog.js';
import sceneManager from '../core/SceneManager.js';
import eventBus from '../core/EventBus.js';
import focusManager from '../input/FocusManager.js';
import townSystem from '../systems/TownSystem.js';
import inventorySystem from '../systems/InventorySystem.js';
import ShopPanel from '../ui/components/ShopPanel.js';
import CraftingPanel from '../ui/components/CraftingPanel.js';
import ReforgePanel from '../ui/components/ReforgePanel.js';
import GamblerPanel from '../ui/components/GamblerPanel.js';
import ResetPanel from '../ui/components/ResetPanel.js';
import TavernPanel from '../ui/components/TavernPanel.js';
import InnPanel from '../ui/components/InnPanel.js';

/** Vendor display info. */
const VENDOR_INFO = {
  shop:     { label: 'General Store',     icon: 'S', description: 'Buy and sell equipment' },
  crafting: { label: 'Crafting Workshop', icon: 'C', description: 'Upgrade item quality' },
  reforge:  { label: 'Reforge Anvil',    icon: 'R', description: 'Reroll item affixes' },
  gambler:  { label: 'Gambler\'s Den',   icon: 'G', description: 'Try your luck for rare items' },
  reset:    { label: 'Sage\'s Sanctum',  icon: 'X', description: 'Reset skills or attributes' },
  tavern:   { label: 'Tavern',           icon: 'T', description: 'Hire and manage party members' },
  inn:      { label: 'Inn',              icon: 'I', description: 'Rest and heal your party' },
};

export default class TownScene {
  constructor() {
    this.container = new Container();

    // PixiJS elements
    this._bg = null;

    // Viewport
    this._width = 800;
    this._height = 600;

    // DOM elements
    this._townOverlay = null;

    // Scene data
    this._townId = null;
    this._town = null;
    this._party = null;
    this._returnScene = 'WorldTravel';
    this._returnData = {};

    // Active panel
    this._activePanel = null;

    // Event handlers
    this._eventHandlers = {};
  }

  async init() {
    debugLog.info('TownScene: init');
  }

  /**
   * Enter the town scene.
   * @param {object} data - { townId, party, returnScene, returnData }
   */
  async enter(data) {
    debugLog.event('TownScene: enter');

    // Get viewport
    const canvas = document.getElementById('game-canvas');
    if (canvas) {
      this._width = canvas.clientWidth || 800;
      this._height = canvas.clientHeight || 600;
    }

    // Store scene data
    this._townId = data?.townId || 'greenhollow_village';
    this._town = townSystem.getTown(this._townId);
    this._party = data?.party || window.__debug?.party || null;
    this._returnScene = data?.returnScene || 'WorldTravel';
    this._returnData = data?.returnData || {};

    if (!this._town) {
      debugLog.error(`TownScene: Town not found: ${this._townId}`);
      this._leaveTown();
      return;
    }

    // Init inventory system with party
    if (this._party) {
      inventorySystem.init(this._party);
    }

    // Build PixiJS background
    this._buildBackground();

    // Build DOM
    this._buildDOM();

    // Subscribe to events
    this._subscribeEvents();

    // Register debug commands
    this._registerDebugCommands();

    // Set up focus
    focusManager.pushGroup('town-menu', this._townOverlay);

    // Expose for testing
    if (window.__debug) {
      window.__debug.townScene = this;
      window.__debug.townSystem = townSystem;
    }
  }

  exit() {
    debugLog.event('TownScene: exit');

    this._unsubscribeEvents();

    // Close any active panel
    if (this._activePanel) {
      this._activePanel.close();
      this._activePanel = null;
    }

    // Remove DOM
    if (this._townOverlay && this._townOverlay.parentNode) {
      this._townOverlay.parentNode.removeChild(this._townOverlay);
    }
    this._townOverlay = null;

    // Clear caches
    townSystem.clearCaches();

    focusManager.clear();
  }

  update(dt) {
    // Nothing per-frame for town
  }

  resize(w, h) {
    this._width = w;
    this._height = h;
    if (this._bg) {
      this._bg.clear();
      this._drawBackground();
    }
  }

  // ── PixiJS Background ─────────────────────────────

  _buildBackground() {
    this._bg = new Graphics();
    this._drawBackground();
    this.container.addChild(this._bg);
  }

  _drawBackground() {
    if (!this._bg) return;
    this._bg.clear();

    // Simple gradient-like background for the town
    this._bg.rect(0, 0, this._width, this._height);
    this._bg.fill({ color: 0x1a1a2e });

    // Ground area
    const groundY = this._height * 0.65;
    this._bg.rect(0, groundY, this._width, this._height - groundY);
    this._bg.fill({ color: 0x2a3a2a });

    // Simple building silhouettes
    const buildingCount = 5;
    const spacing = this._width / (buildingCount + 1);
    for (let i = 0; i < buildingCount; i++) {
      const x = spacing * (i + 0.5);
      const bw = 60 + Math.random() * 40;
      const bh = 80 + Math.random() * 60;
      this._bg.rect(x - bw / 2, groundY - bh, bw, bh);
      this._bg.fill({ color: 0x2a2a3e + i * 0x050505 });

      // Roof
      this._bg.moveTo(x - bw / 2 - 10, groundY - bh);
      this._bg.lineTo(x, groundY - bh - 30);
      this._bg.lineTo(x + bw / 2 + 10, groundY - bh);
      this._bg.closePath();
      this._bg.fill({ color: 0x443322 });
    }
  }

  // ── DOM Construction ──────────────────────────────

  _buildDOM() {
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._townOverlay = document.createElement('div');
    this._townOverlay.className = 'town-overlay';
    this._townOverlay.id = 'town-overlay';
    this._townOverlay.setAttribute('data-scene', 'Town');

    // Town header
    const header = document.createElement('div');
    header.className = 'town-header';

    const title = document.createElement('h1');
    title.className = 'town-title';
    title.textContent = this._town.name;
    header.appendChild(title);

    const desc = document.createElement('p');
    desc.className = 'town-description';
    desc.textContent = this._town.description;
    header.appendChild(desc);

    // Gold display
    if (this._party) {
      const goldBar = document.createElement('div');
      goldBar.className = 'town-gold-bar';
      goldBar.id = 'town-gold-display';
      goldBar.textContent = `Gold: ${this._party.gold}`;
      header.appendChild(goldBar);
    }

    this._townOverlay.appendChild(header);

    // Vendor buttons grid
    const vendorGrid = document.createElement('div');
    vendorGrid.className = 'town-vendor-grid';

    for (const vendorType of this._town.vendors) {
      const info = VENDOR_INFO[vendorType] || { label: vendorType, icon: '?', description: '' };

      const btn = document.createElement('button');
      btn.className = 'town-vendor-btn';
      btn.setAttribute('data-focusable', 'true');
      btn.setAttribute('data-vendor', vendorType);

      const icon = document.createElement('span');
      icon.className = 'town-vendor-icon';
      icon.textContent = info.icon;
      btn.appendChild(icon);

      const label = document.createElement('span');
      label.className = 'town-vendor-label';
      label.textContent = info.label;
      btn.appendChild(label);

      const descEl = document.createElement('span');
      descEl.className = 'town-vendor-desc';
      descEl.textContent = info.description;
      btn.appendChild(descEl);

      btn.addEventListener('click', () => this._openVendor(vendorType));
      vendorGrid.appendChild(btn);
    }

    this._townOverlay.appendChild(vendorGrid);

    // Leave town button
    const leaveBtn = document.createElement('button');
    leaveBtn.className = 'town-leave-btn';
    leaveBtn.id = 'town-leave-btn';
    leaveBtn.setAttribute('data-focusable', 'true');
    leaveBtn.textContent = 'Leave Town';
    leaveBtn.addEventListener('click', () => this._leaveTown());
    this._townOverlay.appendChild(leaveBtn);

    uiRoot.appendChild(this._townOverlay);
  }

  // ── Vendor Panel Opening ──────────────────────────

  _openVendor(vendorType) {
    // Close any existing panel
    if (this._activePanel) {
      this._activePanel.close();
      this._activePanel = null;
    }

    const panelOpts = {
      party: this._party,
      townId: this._townId,
      onClose: () => {
        this._activePanel = null;
        this._updateGoldDisplay();
        // Restore town menu focus
        focusManager.clear();
        focusManager.pushGroup('town-menu', this._townOverlay);
      },
    };

    switch (vendorType) {
      case 'shop':
        this._activePanel = new ShopPanel(panelOpts);
        break;
      case 'crafting':
        this._activePanel = new CraftingPanel(panelOpts);
        break;
      case 'reforge':
        this._activePanel = new ReforgePanel(panelOpts);
        break;
      case 'gambler':
        this._activePanel = new GamblerPanel(panelOpts);
        break;
      case 'reset':
        this._activePanel = new ResetPanel(panelOpts);
        break;
      case 'tavern':
        this._activePanel = new TavernPanel(panelOpts);
        break;
      case 'inn':
        this._activePanel = new InnPanel(panelOpts);
        break;
      default:
        debugLog.warn(`TownScene: Unknown vendor type: ${vendorType}`);
        return;
    }

    if (this._activePanel) {
      this._activePanel.open();
    }
  }

  // ── Gold Display Update ───────────────────────────

  _updateGoldDisplay() {
    const el = document.getElementById('town-gold-display');
    if (el && this._party) {
      el.textContent = `Gold: ${this._party.gold}`;
    }
  }

  // ── Navigation ────────────────────────────────────

  _leaveTown() {
    debugLog.event('TownScene: leaving town');
    sceneManager.transition(this._returnScene, this._returnData);
  }

  // ── Events ────────────────────────────────────────

  _subscribeEvents() {
    this._eventHandlers.onCancel = () => {
      if (this._activePanel) {
        this._activePanel.close();
        this._activePanel = null;
        this._updateGoldDisplay();
        focusManager.clear();
        focusManager.pushGroup('town-menu', this._townOverlay);
      } else {
        this._leaveTown();
      }
    };
    eventBus.on('action:cancel', this._eventHandlers.onCancel);

    // Listen for focusgroup cancelled events
    this._eventHandlers.onFocusCancelled = (e) => {
      if (e.detail?.name === 'town-menu') {
        this._leaveTown();
      }
    };
    if (this._townOverlay) {
      this._townOverlay.addEventListener('focusgroup:cancelled', this._eventHandlers.onFocusCancelled);
    }
  }

  _unsubscribeEvents() {
    if (this._eventHandlers.onCancel) {
      eventBus.off('action:cancel', this._eventHandlers.onCancel);
    }
    this._eventHandlers = {};
  }

  // ── Debug Commands ────────────────────────────────

  _registerDebugCommands() {
    const dc = window.__debug?.console;
    if (!dc) return;

    dc.registerCommand('entertown', (args) => {
      const townId = args[0];
      if (!townId) return 'Usage: entertown <town_id>';

      const town = townSystem.getTown(townId);
      if (!town) {
        const allTowns = townSystem.getAllTowns().map(t => t.id).join(', ');
        return `Town not found: ${townId}. Available: ${allTowns}`;
      }

      sceneManager.transition('Town', {
        townId,
        party: this._party || window.__debug?.party,
      });

      return `Entering ${town.name}...`;
    }, 'Enter a town: entertown <town_id>');

    dc.registerCommand('gold', (args) => {
      const amount = parseInt(args[0], 10);
      if (isNaN(amount)) return 'Usage: gold <amount>';
      const party = this._party || window.__debug?.party;
      if (!party) return 'No party available.';
      party.gold += amount;
      this._updateGoldDisplay();
      return `Gold: ${party.gold} (${amount >= 0 ? '+' : ''}${amount})`;
    }, 'Add/remove gold: gold <amount>');
  }
}
